/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';

// Ant Design
import {
  Row,
  Col,
  Spin,
  Form,
  Modal,
} from 'antd';
import {
  CheckSquareOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

// utils/apis/constants
import { checkToken } from 'api/utils';
import currencyFormatter from 'utils/currencyFormatter';
import { getCalculosAnualesISREmpleados, postURLCalculoAnualISR } from 'api/nominas/calculoISRAnual/isrCalculosAnualesEmpleados';
import { getEmpleadosFiltered } from 'api/estructuraOrganizacional/empleados';
import { getAdministracionDePlazas } from 'api/catalogos/admnistracionDePlazas';
import { getPlazas } from 'api/catalogos/plazas';
import { getPuestos } from 'api/catalogos/puestos';
import { getAreas } from 'api/catalogos';
import { getCalculosAnualesISREmpleadosCalculos } from 'api/nominas/calculoISRAnual/isrCalculosAnualesEmpleadosCalculos';
import { getCalculosAnualesISREmpleadosPercepciones } from 'api/nominas/calculoISRAnual/isrCalculosAnualesEmpleadosPercepciones';
import { getPercepciones } from 'api/nominas/percepciones/percepciones';
import { formatReceivedValues } from 'utils/formatValues';
import { ESTATUS_AUTORIZADO } from 'utils/constants';

// Normalizers
import { areaBuscar, normalizeData } from 'utils/normalizers';

// componentes
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import PercentageInput from 'components/PercentageInput';
import CurrencyInput from 'components/CurrencyInput';

export const calculoAnualISRPermission = {
  permissionModel: 'isrcalculoanual',
  catalogsModels: [],
  tabsModels: [],
};

const IsrCalculoAnualDetalle = ({
  permission,
  selectedRowKeyEncabezado,
  onClickEditEncabezado,
  fetchDataEncabezado,
  estatusInactivoAutorizadoCancelado,
}) => {
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [empleados, setEmpleados] = useState([]);
  const [adminPlazas, setAdminPlazas] = useState([]);
  const [plazas, setPlazas] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [percepciones, setPercepciones] = useState([]);
  const [calculosISREmpleadosPercepciones, setCalculosISREmpleadosPercepciones] = useState([]);
  const [disableConsulta, setDisableConsulta] = useState(false);
  const [form] = Form.useForm();

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
        noEmpleado: e?.empleado,
        nombreEmpleado: e?.empleado,
        puestoEmpleado: e?.empleado,
        areaEmpleado: e?.empleado,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getCalculosAnualesISREmpleados({ isr_calculo_anual: selectedRowKeyEncabezado });
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _empleados,
        _adminPlazas,
        _plazas,
        _puestos,
        _areas,
        _percepciones,
        _data,
      ] = await Promise.all([
        getEmpleadosFiltered(),
        getAdministracionDePlazas(),
        getPlazas(),
        getPuestos(),
        getAreas({ estados_globales: ESTATUS_AUTORIZADO }),
        getPercepciones(),
        getCalculosAnualesISREmpleados({ isr_calculo_anual: selectedRowKeyEncabezado }),
      ]);
      const formattedURS = normalizeData(_areas);
      if (mounted) {
        setEmpleados(_empleados);
        setAdminPlazas(_adminPlazas);
        setPlazas(_plazas);
        setPuestos(_puestos);
        setAreas(formattedURS);
        setPercepciones(_percepciones);
        setData(formatReceivedClone(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
  };

  const onClickConsulta = async () => {
    onCancel();
    const _percepcionesEmpleado = await getCalculosAnualesISREmpleadosPercepciones({ isr_calculo_anual_empleado: selectedRowKeys[0] });
    const _calculosEmpleado = await getCalculosAnualesISREmpleadosCalculos({ isr_calculo_anual_empleado: selectedRowKeys[0] });
    setCalculosISREmpleadosPercepciones(_percepcionesEmpleado);
    form.setFieldsValue(_calculosEmpleado[0]);
    setVisible(true);
  };

  const onClickCalcular = async () => {
    setLoading(true);
    await postURLCalculoAnualISR(selectedRowKeyEncabezado);
    fetchData();
    onClickEditEncabezado();
    fetchDataEncabezado();
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    if (record?.calculo_isr) {
      setDisableConsulta(false);
    } else {
      setDisableConsulta(true);
    }
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'ISR Calculado',
      dataIndex: 'calculo_isr',
      key: 'calculo_isr',
      width: 160,
      align: 'center',
      render: (val) => (
        val ? (
          <div
            style={{
              backgroundColor: '#99EEB6',
              borderRadius: '50px',
              height: '25px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            CALCULADO
          </div>
        ) : (
          <div
            style={{
              backgroundColor: '#ED9682',
              borderRadius: '50px',
              height: '25px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            NO CALCULADO
          </div>
        )
      ),
    },
    {
      titleText: 'No. de Empleado',
      dataIndex: 'noEmpleado',
      key: 'noEmpleado',
      width: 200,
      render: (val) => {
        const _empleado = empleados?.find((emp) => emp?.id === val);
        return `${_empleado?.numero_de_empleado || ''}`;
      },
    },
    {
      titleText: 'Nombre del Empleado',
      dataIndex: 'nombreEmpleado',
      key: 'nombreEmpleado',
      width: 300,
      render: (val) => {
        const _empleado = empleados?.find((emp) => emp?.id === val);
        return `${_empleado?.nombre || ''} ${_empleado?.apellido_materno || ''} ${_empleado?.apellido_paterno || ''}`;
      },
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puestoEmpleado',
      key: 'puestoEmpleado',
      width: 300,
      render: (val) => {
        const _adminPlaza = adminPlazas?.find((admnPl) => admnPl?.empleado === val);
        const _plaza = plazas?.find((pl) => pl?.id === _adminPlaza?.plaza);
        const _puesto = puestos?.find((p) => p?.id === _plaza?.puesto);
        return `${_puesto?.descripcion || ''}`;
      },
    },
    {
      titleText: 'Área',
      dataIndex: 'areaEmpleado',
      key: 'areaEmpleado',
      width: 300,
      render: (val) => {
        const _adminPlaza = adminPlazas?.find((admPl) => admPl?.empleado === val);
        const _plaza = plazas?.find((pl) => pl?.id === _adminPlaza?.plaza);
        const _area = areaBuscar(_plaza?.area_content_id, areas);
        return `${_area?.tableTitle || ''}`;
      },
    },
    {
      titleText: 'Importe Total de Percepciones',
      dataIndex: 'importe_total_percepciones',
      key: 'importe_total_percepciones',
      width: 250,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Fecha de Alta después del 01/Ene',
      dataIndex: 'fecha_de_alta_despues',
      key: 'fecha_de_alta_despues',
      width: 300,
    },
    {
      titleText: 'Fecha de Baja antes del 31/Dic',
      dataIndex: 'fecha_de_baja_antes',
      key: 'fecha_de_baja_antes',
      width: 300,
    },
    {
      titleText: 'Situación para NO Cálculo Anual de ISR',
      dataIndex: 'motivos_de_no_calculo',
      key: 'motivos_de_no_calculo',
      width: 300,
    },
    {
      titleText: 'Fecha de Aviso',
      dataIndex: 'fecha_de_aviso',
      key: 'fecha_de_aviso',
      width: 200,
    },
    {
      titleText: 'ISR a Cargo',
      dataIndex: 'isr_a_cargo',
      key: 'isr_a_cargo',
      width: 200,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'ISR a Favor',
      dataIndex: 'isr_a_favor',
      key: 'isr_a_favor',
      width: 200,
      render: (val) => currencyFormatter(val || 0),
    },
  ];

  const columnsPercepciones = [
    {
      titleText: 'Percepción',
      dataIndex: 'percepcion',
      key: 'percepcion',
      width: 300,
      render: (val) => {
        const percepcion = percepciones?.find((p) => p?.id === val);
        return `${percepcion?.clave} - ${percepcion?.descripcion}`;
      },
    },
    {
      titleText: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 200,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Exento',
      dataIndex: 'exento',
      key: 'exento',
      width: 200,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Gravado',
      dataIndex: 'gravado',
      key: 'gravado',
      width: 200,
      render: (val) => currencyFormatter(val || 0),
    },
  ];

  return (
    <Row className="container">
      <Spin tip="cargando...." spinning={loading}>
        <Table
          data={data}
          cols={columns}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          customActions={[
            {
              onClick: onClickCalcular,
              text: 'Calcular',
              icon: loading ? <LoadingOutlined /> : <ReloadOutlined />,
              disabledBySelection: false,
              disabled: estatusInactivoAutorizadoCancelado,
            },
            {
              onClick: onClickConsulta,
              text: 'Consulta de Calculo',
              icon: <CheckSquareOutlined />,
              disabledBySelection: false,
              disabled: !selectedRowKeys.length || disableConsulta,
            },
          ]}
          allowExportParam={false}
          permission={permission}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label="Consulta de Cálculo"
              onCancel={onCancel}
              loading={loading}
            />
            )}
          onCancel={onCancel}
          forceRender
          width="85%"
        >
          <Table
            data={calculosISREmpleadosPercepciones}
            cols={columnsPercepciones}
            allowSearch={false}
            allowExportParam={false}
            permission={permission}
          />
          <Form
            layout="vertical"
            form={form}
            scrollToFirstError
            name="detalle"
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <CurrencyInput
                  label="No. de Empleados sin Cálculo Anual de ISR"
                  name="numero_de_empleados_sin_calculo"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <CurrencyInput
                  label="Limite Inferior"
                  name="limite_inferior"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <CurrencyInput
                  label="Excedente"
                  name="excedente"
                  disabled
                />
              </Col>
            </Row>
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <PercentageInput
                  label="Porcentaje sobre Excedente"
                  name="porcentaje_sobre_excedente"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <CurrencyInput
                  label="Impuesto Marginal"
                  name="impuesto_marginal"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <CurrencyInput
                  label="Cuota Fija"
                  name="cuota_fija"
                  disabled
                />
              </Col>
            </Row>
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <CurrencyInput
                  label="ISR Causado"
                  name="isr_causado"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <CurrencyInput
                  label="Retenciones Efectuadas"
                  name="retenciones_efectuadas"
                  disabled
                />
              </Col>
              <Col xs={24} sm={24} md={8}>
                <CurrencyInput
                  label="ISR a Cargo"
                  name="isr_a_cargo"
                  disabled
                />
              </Col>
            </Row>
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <CurrencyInput
                  label="ISR a Favor"
                  name="isr_a_favor"
                  disabled
                />
              </Col>
            </Row>
          </Form>
        </Modal>
      </Spin>
    </Row>
  );
};

export default IsrCalculoAnualDetalle;
